// Daten der Einheit-Seite (E7). Wie die Objektseite: jeder Bereich lädt und scheitert für sich.
// :einheitId ist die id aus objekt_einheiten; ältere Links mit der Bezeichnung funktionieren weiter.

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase/client';
import type { MieterDetailRoh } from './objektDetail';
import { gehoertZurEinheit } from './einheitDetail';
import {
  zusatzLader, type DienstleisterLinkZeile, type DienstleisterZeile, type DokumentZeile, type EigentuemerZeile, type FachDokumentZeile, type NotizZeile, type ObjektRoh,
  type TerminZeile, type TicketZeile, type UebergabeZeile, type VertragZeile, type ZaehlerZeile,
} from './useObjektDetail';
import { ladeDienstleisterFuerObjekt } from '../dienstleister/api';
import { aktuelleZeile, type EinheitEigZeitraum } from '../eigentuemer/einheitEigentum';
import { heuteIso } from './format';

/** objekt_einheiten-Zeile (select '*'): bekannte Felder typisiert, Kauf/Finanzierung über das Feld-Objekt. */
export interface EinheitRoh extends Record<string, unknown> {
  id: string;
  objekt_id: string;
  bezeichnung: string | null;
  typ: string | null;
  wohnflaeche: number | string | null;
  nutzflaeche?: number | string | null;
  zimmer?: number | string | null;
  etage?: string | null;
  haus_id: string | null;
  status?: string | null;
  ausstattung?: string | null;
  /** #499: Zimmer gehört zu dieser Wohnung */
  uebergeordnete_einheit_id?: string | null;
}

/** mieter-Zeile (select '*'), die Felder, die die Einheit-Seite liest. */
export interface MietverhaeltnisRoh extends MieterDetailRoh {
  anzahl_bewohner?: number | null;
  kaution?: number | string | null;
  kaution_ist?: number | string | null;
  kaution_saldo?: number | string | null;
  kaution_art?: string | null;
  kaution_gegenstand?: string | null;
  kaution_raten?: number | null;
  kaution_iban?: string | null;
  kaution_gueltig_ab?: string | null;
  kaution_gueltig_bis?: string | null;
  sepa_aktiv?: boolean | null;
  sepa_mandatsreferenz?: string | null;
  sepa_mandatsdatum?: string | null;
  mieter_iban?: string | null;
  mieter_kontoinhaber?: string | null;
  miete_faellig_tag?: number | null;
  mietintervall?: string | null;
  vertragsart?: string | null;
}

export interface MitmieterZeile { hauptId: string; id: string; vorname: string | null; nachname: string | null; firma_name: string | null; email: string | null; telefon: string | null; mobil: string | null }

export interface EinheitEigentuemerZeile {
  eigentuemer_id: string;
  mea: number | null;
  hausgeld_vorschuss: number | null;
  ruecklage_vorschuss: number | null;
  sev_status: string | null;
  eigentuemer: { id: string; anrede: string | null; vorname: string | null; nachname: string | null; firma_name: string | null; email: string | null; telefon: string | null } | null;
}

export interface RechnungZeile {
  id: string;
  rechnungsnummer: string | null;
  bezeichnung: string | null;
  unternehmen: string | null;
  brutto: number | string | null;
  datum: string | null;
  status: string | null;
  kategorie?: string | null;
  richtung: 'eingang' | 'ausgang' | null;
  bezahlt_timestamp: string | null;
  dokument_id: string | null;
}

export interface EinheitDokumentZeile extends DokumentZeile { mieter_id: string | null }

export interface EinheitDetailDaten {
  objekt: ObjektRoh;
  einheit: EinheitRoh;
  /** alle Mietverhältnisse der Einheit (aktuelle und ehemalige) */
  mieter: MietverhaeltnisRoh[];
  mitmieter: MitmieterZeile[];
  eigentuemer: EinheitEigentuemerZeile | null;
  objektEigentuemer: EigentuemerZeile[];
  notizen: NotizZeile[];
  dokumente: EinheitDokumentZeile[];
  rechnungen: RechnungZeile[];
  zaehler: (ZaehlerZeile & { mieter_id?: string | null })[];
  /** Tickets der Einheit oder ihrer Mieter */
  tickets: TicketZeile[];
  /** Mietverträge (vertraege) der Mieter dieser Einheit */
  vertraege: VertragZeile[];
  uebergaben: UebergabeZeile[];
  /** Versicherungs- und Wartungsdokumente des Objekts (gelten für alle Einheiten) */
  fachDokumente: FachDokumentZeile[];
  /** Termine des Objekts (Wartung, Versammlung) */
  termine: TerminZeile[];
  dienstleister: DienstleisterZeile[];
  dienstleisterLinks: DienstleisterLinkZeile[];
  /** Änderungen an den Mietern der Einheit (entity_audit) */
  audit: AuditZeile[];
}

export interface AuditZeile { id: string; datensatz_id: string; aktion: string; feld: string | null; alt_wert: string | null; neu_wert: string | null; geaendert_am: string }

export type EinheitBereich = Exclude<keyof EinheitDetailDaten, 'objekt' | 'einheit'>;

export type EinheitZustand =
  | { status: 'laedt' }
  | { status: 'fehlt' }
  | { status: 'fehler'; meldung: string }
  | { status: 'ok'; daten: EinheitDetailDaten; fehler: Partial<Record<EinheitBereich, string>> };

type Ergebnis<T> = { ok: true; wert: T } | { ok: false; fehler: string };

async function versuche<T>(f: () => PromiseLike<T> | T): Promise<Ergebnis<T>> {
  try { return { ok: true, wert: await f() }; } catch (e) { return { ok: false, fehler: e instanceof Error ? e.message : 'Unbekannter Fehler' }; }
}

function daten<T>(r: { data: T | null; error: { message: string } | null }): T {
  if (r.error) throw new Error(r.error.message);
  return (r.data ?? []) as T;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface Kontext { objektId: string; einheit: EinheitRoh; mieterIds: string[] }

const LADER: { [K in EinheitBereich]: (k: Kontext) => Promise<EinheitDetailDaten[K]> } = {
  mieter: async ({ objektId, einheit }) => {
    const alle = daten(await supabase.from('mieter').select('*').eq('objekt_id', objektId)) as MietverhaeltnisRoh[];
    return alle.filter(m => gehoertZurEinheit(m, einheit));
  },
  mitmieter: async ({ mieterIds }) => {
    if (mieterIds.length === 0) return [];
    const links = daten(await supabase.from('entity_links').select('source_id, target_id')
      .eq('source_type', 'mieter').eq('link_type', 'mitmieter').in('source_id', mieterIds)) as { source_id: string; target_id: string }[];
    if (links.length === 0) return [];
    const zeilen = daten(await supabase.from('mieter').select('id, vorname, nachname, firma_name, email, telefon, mobil')
      .in('id', links.map(l => l.target_id))) as Omit<MitmieterZeile, 'hauptId'>[];
    return links.flatMap(l => {
      const z = zeilen.find(x => x.id === l.target_id);
      return z ? [{ ...z, hauptId: l.source_id }] : [];
    });
  },
  eigentuemer: async ({ einheit }) => {
    // Historie: Vorbesitzer, geplante Übernahmen und Miteigentümer – angezeigt wird die laufende Hauptzeile
    const r = await supabase.from('einheit_eigentuemer')
      .select('eigentuemer_id, mea, hausgeld_vorschuss, ruecklage_vorschuss, sev_status, vertrag_ab, vertrag_bis, eigentuemer:eigentuemer_id(id, anrede, vorname, nachname, firma_name, email, telefon)')
      .eq('einheit_id', einheit.id);
    if (r.error) throw new Error(r.error.message);
    return aktuelleZeile((r.data ?? []) as unknown as (EinheitEigentuemerZeile & EinheitEigZeitraum)[], heuteIso());
  },
  objektEigentuemer: async ({ objektId }) => daten(await supabase.from('v_objekt_eigentuemer').select('*').eq('objekt_id', objektId)) as EigentuemerZeile[],
  notizen: async ({ einheit }) => daten(await supabase.from('notizen').select('id, text, created_at').eq('einheit_id', einheit.id)
    .order('created_at', { ascending: false })) as NotizZeile[],
  dokumente: async ({ objektId, mieterIds }) => {
    // wie bisher: Dokumente der Mieter dieser Einheit (dokumente hat keine Spalte einheit_id)
    if (mieterIds.length === 0) return [];
    return daten(await supabase.from('dokumente')
      .select('id, titel, datei_name, datei_typ, datei_url, onedrive_url, gdrive_file_id, kategorie, created_at, mieter_id')
      .eq('objekt_id', objektId).eq('is_deleted', false).in('mieter_id', mieterIds)
      .order('created_at', { ascending: false }).limit(100)) as EinheitDokumentZeile[];
  },
  // '*' wie bisher: nicht jede Spalte (z. B. kategorie) existiert in jeder Umgebung
  rechnungen: async ({ einheit }) => daten(await supabase.from('rechnungen')
    .select('*')
    .eq('einheit_id', einheit.id).is('archiviert_am', null).order('datum', { ascending: false })) as RechnungZeile[],
  zaehler: async ({ objektId }) => daten(await supabase.from('zaehler').select('*').eq('objekt_id', objektId).order('typ')) as EinheitDetailDaten['zaehler'],
  tickets: async ({ objektId, einheit, mieterIds }) => (await zusatzLader.tickets(objektId))
    .filter(t => t.einheit_id === einheit.id || (!!t.mieter_id && mieterIds.includes(t.mieter_id))),
  vertraege: async ({ objektId, einheit, mieterIds }) => (await zusatzLader.vertraege(objektId))
    .filter(v => (!!v.mieter_id && mieterIds.includes(v.mieter_id)) || (!!einheit.bezeichnung && (v.einheit ?? '').trim() === einheit.bezeichnung.trim())),
  uebergaben: async ({ objektId, einheit }) => (await zusatzLader.uebergaben(objektId)).filter(u => u.einheit_id === einheit.id),
  fachDokumente: async ({ objektId }) => zusatzLader.fachDokumente(objektId),
  termine: async ({ objektId }) => daten(await supabase.from('termine').select('*').eq('objekt_id', objektId).order('start_datum', { ascending: true })) as TerminZeile[],
  dienstleister: async ({ objektId }) => ladeDienstleisterFuerObjekt(objektId),
  dienstleisterLinks: async ({ objektId }) => zusatzLader.dienstleisterLinks(objektId),
  audit: async ({ mieterIds }) => {
    if (mieterIds.length === 0) return [];
    return daten(await supabase.from('entity_audit').select('id, datensatz_id, aktion, feld, alt_wert, neu_wert, geaendert_am')
      .eq('tabelle', 'mieter').in('datensatz_id', mieterIds).order('geaendert_am', { ascending: false }).limit(200)) as AuditZeile[];
  },
};

const BEREICHE = Object.keys(LADER) as EinheitBereich[];

function ladeBereich(b: EinheitBereich, k: Kontext): Promise<unknown> {
  return (LADER[b] as (k: Kontext) => Promise<unknown>)(k);
}

const LEER: Omit<EinheitDetailDaten, 'objekt' | 'einheit'> = {
  mieter: [], mitmieter: [], eigentuemer: null, objektEigentuemer: [], notizen: [], dokumente: [], rechnungen: [], zaehler: [],
  tickets: [], vertraege: [], uebergaben: [], fachDokumente: [], termine: [], dienstleister: [], dienstleisterLinks: [], audit: [],
};

export function useEinheitDetail(objektId: string, einheitId: string): {
  zustand: EinheitZustand;
  neuLaden: (...bereiche: (EinheitBereich | 'einheit')[]) => void;
} {
  const [zustand, setZustand] = useState<EinheitZustand>({ status: 'laedt' });
  const schluessel = `${objektId}|${einheitId}`;
  const aktuell = useRef(schluessel);
  const kontext = useRef<Kontext | null>(null);

  const ladeAlles = useCallback(async (oid: string, eid: string) => {
    const s = `${oid}|${eid}`;
    const einheitAbfrage = supabase.from('objekt_einheiten').select('*').eq('objekt_id', oid);
    const [obj, ein] = await Promise.all([
      supabase.from('objekte').select('*').eq('id', oid).maybeSingle(),
      (UUID.test(eid) ? einheitAbfrage.eq('id', eid) : einheitAbfrage.eq('bezeichnung', eid)).limit(1),
    ]);
    if (aktuell.current !== s) return;
    if (obj.error || ein.error) { setZustand({ status: 'fehler', meldung: (obj.error ?? ein.error)?.message ?? 'Fehler' }); return; }
    const einheit = ((ein.data ?? []) as EinheitRoh[])[0];
    if (!obj.data || !einheit) { setZustand({ status: 'fehlt' }); return; }

    const basis: Kontext = { objektId: oid, einheit, mieterIds: [] };
    const mieterErg = await versuche(() => LADER.mieter(basis));
    const k: Kontext = { ...basis, mieterIds: mieterErg.ok ? mieterErg.wert.map(m => m.id) : [] };
    kontext.current = k;
    const rest = BEREICHE.filter(b => b !== 'mieter');
    const ergebnisse = await Promise.all(rest.map(b => versuche(() => ladeBereich(b, k))));
    if (aktuell.current !== s) return;

    const d: EinheitDetailDaten = { ...LEER, objekt: obj.data as ObjektRoh, einheit };
    const fehler: Partial<Record<EinheitBereich, string>> = {};
    if (mieterErg.ok) d.mieter = mieterErg.wert; else fehler.mieter = mieterErg.fehler;
    rest.forEach((b, i) => {
      const e = ergebnisse[i];
      if (!e) return;
      if (e.ok) (d as unknown as Record<EinheitBereich, unknown>)[b] = e.wert;
      else fehler[b] = e.fehler;
    });
    setZustand({ status: 'ok', daten: d, fehler });
  }, []);

  useEffect(() => {
    aktuell.current = schluessel;
    setZustand({ status: 'laedt' });
    void ladeAlles(objektId, einheitId);
  }, [schluessel, objektId, einheitId, ladeAlles]);

  const neuLaden = useCallback((...bereiche: (EinheitBereich | 'einheit')[]) => {
    const s = aktuell.current;
    const [oid, eid] = s.split('|') as [string, string];
    // Einheit oder Mieter geändert: alles neu (Mieter-IDs steuern Dokumente und Mitmieter)
    if (bereiche.length === 0 || bereiche.includes('einheit') || bereiche.includes('mieter') || !kontext.current) { void ladeAlles(oid, eid); return; }
    const k = kontext.current;
    const echte = bereiche.filter((b): b is EinheitBereich => b !== 'einheit');
    void (async () => {
      const erg = await Promise.all(echte.map(b => versuche(() => ladeBereich(b, k))));
      if (aktuell.current !== s) return;
      setZustand(z => {
        if (z.status !== 'ok') return z;
        const d = { ...z.daten } as unknown as Record<EinheitBereich, unknown> & EinheitDetailDaten;
        const fehler = { ...z.fehler };
        echte.forEach((b, i) => {
          const e = erg[i];
          if (!e) return;
          if (e.ok) { d[b] = e.wert as never; delete fehler[b]; } else fehler[b] = e.fehler;
        });
        return { status: 'ok', daten: d, fehler };
      });
    })();
  }, [ladeAlles]);

  return { zustand, neuLaden };
}
